import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Send } from "lucide-react";
import { submitLead } from "../lib/api";
import { LOAN_TYPES } from "../lib/data";

const init = { name: "", email: "", phone: "", loan_type: "", amount: "", city: "", message: "" };

export default function LeadForm({ compact = false }) {
  const [form, setForm] = useState(init);
  const [loading, setLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const selectedLoan = searchParams.get("loan");

  useEffect(() => {
    if (LOAN_TYPES.includes(selectedLoan)) setForm((current) => ({ ...current, loan_type: selectedLoan }));
  }, [selectedLoan]);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.loan_type) {
      toast.error("Please fill name, email, phone & loan type.");
      return;
    }
    setLoading(true);
    try {
      await submitLead(form);
      toast.success("Thank you! Our team will reach out shortly.");
      setForm(init);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form
      id="lead"
      onSubmit={onSubmit}
      className="bg-white border border-border p-6 md:p-8 rounded-sm shadow-sm"
      data-testid="lead-form"
    >
      <div className="mb-5">
        <span className="accent-bar mb-3" />
        <h3 className="font-heading text-2xl md:text-3xl font-bold text-navy">Get a Free Funding Consultation</h3>
        <p className="text-sm text-muted-foreground mt-1">Share your details. Our advisors call back within 24 hours.</p>
      </div>

      <div className={`grid ${compact ? "grid-cols-1" : "md:grid-cols-2"} gap-4`}>
        <Input label="Full Name *" name="name" value={form.name} onChange={onChange} testid="lead-name" />
        <Input label="Phone *" name="phone" value={form.phone} onChange={onChange} testid="lead-phone" />
        <Input label="Email *" name="email" type="email" value={form.email} onChange={onChange} testid="lead-email" />
        <div className="flex flex-col gap-1.5">
          <label htmlFor="lead-loan-type" className="text-xs font-semibold text-navy uppercase tracking-wider">Loan Type *</label>
          <select
            id="lead-loan-type"
            name="loan_type"
            value={form.loan_type}
            onChange={onChange}
            data-testid="lead-loan-type"
            className="w-full min-w-0 border border-input bg-white px-3 py-2.5 text-sm rounded-sm focus:outline-none focus:ring-2 focus:ring-navy"
          >
            <option value="">Select loan type</option>
            {LOAN_TYPES.map((t) => (
              <option key={t} value={t} data-testid={`lead-option-${t.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>{t}</option>
            ))}
          </select>
        </div>
        <Input label="Loan Amount (₹)" name="amount" value={form.amount} onChange={onChange} testid="lead-amount" placeholder="e.g. 50 Lakh, 5 Cr" />
        <Input label="City" name="city" value={form.city} onChange={onChange} testid="lead-city" />
      </div>

      <div className="mt-4 flex flex-col gap-1.5">
        <label htmlFor="lead-message" className="text-xs font-semibold text-navy uppercase tracking-wider">Message</label>
        <textarea
          id="lead-message"
          name="message"
          value={form.message}
          onChange={onChange}
          rows={3}
          data-testid="lead-message"
          className="border border-input bg-white px-3 py-2.5 text-sm rounded-sm focus:outline-none focus:ring-2 focus:ring-navy resize-none"
          placeholder="Tell us about your funding requirement"
        />
      </div>

      <button
        type="submit"
        disabled={loading}
        data-testid="lead-submit"
        className="mt-6 w-full bg-brandred hover:bg-brandred-700 disabled:opacity-60 text-white py-3 font-semibold rounded-sm transition-colors flex items-center justify-center gap-2"
      >
        {loading ? <Loader2 className="animate-spin" size={18} /> : <Send size={18} />}
        {loading ? "Submitting..." : "Submit Enquiry"}
      </button>

      <p className="text-[11px] text-muted-foreground mt-3">
        By submitting, you agree to be contacted by Crew Connect FinInvesto regarding your enquiry.
      </p>
    </form>
  );
}

function Input({ label, name, value, onChange, type = "text", testid, placeholder }) {
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={testid} className="text-xs font-semibold text-navy uppercase tracking-wider">{label}</label>
      <input
        id={testid}
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        data-testid={testid}
        className="border border-input bg-white px-3 py-2.5 text-sm rounded-sm focus:outline-none focus:ring-2 focus:ring-navy"
      />
    </div>
  );
}
